"use client";

/**
 * `useShippingRates` — race-safe React behaviour layer for the buyer-side
 * live-rate picker (WS4.2).
 *
 * FSM via discriminated union (react-state-design §4, type-driven-design §9):
 * idle → loading → loaded → (selectRate) → loaded'. Error transitions are
 * terminal until the next `fetchRates` / `reset`.
 *
 * Race safety:
 *  - Every `fetchRates` mints a fresh `AbortController` and aborts the previous
 *    in-flight one (single-flight by latest-wins, concurrency-design §5).
 *  - A monotonic `requestIdRef` distinguishes "is my resolution the latest" —
 *    even when the previous controller has aborted, the awaited promise may
 *    still resolve a tick later; the id check discards the stale outcome.
 *  - The hook never derives state in `useEffect` (HARD GATE / Silent NACK).
 *    `useEffect` is used ONLY to abort the in-flight controller on unmount.
 *
 * Fetch shape:
 *  - The HTTP body is assembled by the `lib/checkout` builder; the hook NEVER
 *    inlines JSON. This is the Sibling-E discipline: positive tests of this
 *    hook call the REAL builder for their expected-body assertions (never a
 *    hand-written body literal that would pin the bug they're catching).
 *  - The response is run through `ShippingRatesResponseSchema.safeParse` as a
 *    defensive client-side narrowing — the server boundary is already the
 *    authoritative Zod parse (memory `feedback_client_parse_is_not_boundary_parse`,
 *    NOT a duplicated "boundary parse"). On safeParse failure → typed
 *    `RESPONSE_SHAPE_INVALID` state, separate from real HTTP errors.
 *
 * Error code coverage:
 *   Mirrors every code `app/api/shipping/rates/route.ts` can return
 *   (including `SHIPPING_SANCTIONED_DESTINATION` and
 *   `PARCEL_BELOW_CARRIER_MINIMUM`, T13 2026-07-23), plus client-only codes
 *   (`RESPONSE_SHAPE_INVALID`, `NETWORK_ERROR`).
 *   `AbortError` does NOT transition to error — it's a happy-path outcome
 *   (concurrency-design §5.2).
 *
 *   Two origin-tier codes (Option D, locked 2026-06-20):
 *     - `OUT_OF_ORIGIN` (422): the SELLER's origin country isn't in
 *       `SHIPPO_ORIGIN_SUPPORT`. WS8.1 eligibility gate prevents listing
 *       creation from unsupported origins; reaching this in checkout means
 *       the listing predates the gate or the seller moved countries
 *       post-listing.
 *     - `NO_RATES_TO_DESTINATION` (400): supported origin, but Shippo
 *       returned 0 rates for the BUYER's destination (sanctioned country,
 *       embargoed lane, or provider gap). Explicit failure per PLAN §A
 *       "Checkout fallback" — no silent legacy substitution. This is the
 *       authoritative destination gate; the buyer-side builder is a pure
 *       normaliser and never pre-rejects (the older `OUT_OF_REGION` client
 *       throw was removed in the Phase 1a → 2a left-fix, since US-origin
 *       sellers ship worldwide).
 *   Both carry an extra payload key (`originCountry` / `country`) lifted
 *   from the route's `details` block so the UI can render country-specific
 *   copy without re-parsing `message`.
 *
 * @see .tondocs/shippo/PLAN.md §D WS4 — workstream
 * @see .tondocs/shippo/PLAN.md §L — rate is ESTIMATE (no durable lock here)
 * @see .tondocs/shippo/PLAN.md §J.5 — single-seller invariant (one sellerId)
 * @see .tondocs/skills/react-state-design.md §4, §9, §10 — FSM + stable cbs + race
 * @see .tondocs/skills/type-driven-design.md §3, §9 — never-probe + FSM
 * @see .tondocs/skills/concurrency-design.md §5 — AbortController + happy-path cancel
 * @see .tondocs/skills/hex.md §1 — hook is delivery, builder is domain
 * @see lib/checkout/build-shipping-rates-request.ts — the builder
 * @see packages/contract/src/shipping-rates.ts — wire contract
 * @see app/api/shipping/rates/route.ts — server-side error codes
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import {
  ShippingRatesResponseSchema,
  type ShippingRatesQuote,
} from "@kraahl/contract";
import {
  buildShippingRatesRequest,
  type ShippingRateIntent,
} from "@/lib/checkout/build-shipping-rates-request";

// ---------------------------------------------------------------------------
// Public types — FSM state + selection + error codes
// ---------------------------------------------------------------------------

/**
 * Every error code the hook can emit. Union of:
 *  - Codes the route returns (`UNAUTHORIZED`, `MULTI_SELLER_FORBIDDEN`,
 *    `VALIDATION_ERROR`, `OUT_OF_ORIGIN`, `NO_RATES_TO_DESTINATION`,
 *    `INVALID_ADDRESS`, `MISSING_PARCEL_DIMS`, `MISSING_FROM_ADDRESS`,
 *    `PROVIDER_ERROR`, `INTERNAL_ERROR`, `SHIPPING_SANCTIONED_DESTINATION`,
 *    `PARCEL_BELOW_CARRIER_MINIMUM`) — see `app/api/shipping/rates/route.ts`.
 *  - Client-only codes the route never produces:
 *    - `RESPONSE_SHAPE_INVALID`: server contract drift, safeParse failed.
 *    - `NETWORK_ERROR`: fetch threw (offline, DNS, TLS) and it was NOT an
 *      `AbortError` (aborts are silently discarded — see hook docs).
 *
 * Mirrors §13 of the spec; a left-fix of the route's error vocabulary must
 * land here in the same change. Any switch on this union ends with a
 * `default: unreachable(x: never)` probe to enforce that.
 *
 * Note: `OUT_OF_REGION` (client-only, NA-whitelist throw from the builder)
 * was removed when destination gating moved server-side in the Phase 1a → 2a
 * left-fix. The authoritative destination gate is now `NO_RATES_TO_DESTINATION`
 * from the route — see `lib/shipping/rates.ts::RateFetchResult` ADT.
 *
 * `SHIPPING_SANCTIONED_DESTINATION` (OFAC gate, §O.2.7) and
 * `PARCEL_BELOW_CARRIER_MINIMUM` (PARCEL_MIN_DIMS.md §5) were added T13
 * 2026-07-23 — the route emitted both since before this union existed, but
 * `normaliseRouteCode` was silently coercing them to `INTERNAL_ERROR` before
 * the buyer ever saw a translated message
 * ([[feedback_left_fix_audit_downstream_branches]]).
 */
export type ShippingRatesErrorCode =
  | "UNAUTHORIZED"
  | "MULTI_SELLER_FORBIDDEN"
  | "VALIDATION_ERROR"
  | "OUT_OF_ORIGIN"
  | "NO_RATES_TO_DESTINATION"
  | "INVALID_ADDRESS"
  | "MISSING_PARCEL_DIMS"
  | "MISSING_FROM_ADDRESS"
  | "PROVIDER_ERROR"
  | "INTERNAL_ERROR"
  | "RESPONSE_SHAPE_INVALID"
  | "NETWORK_ERROR"
  | "SHIPPING_SANCTIONED_DESTINATION"
  | "PARCEL_BELOW_CARRIER_MINIMUM";

/**
 * Buyer's chosen rate — promoted to a typed export so UI consumers don't have
 * to reach into `Rate[]` internals (resolves WS4.1 Open Q #1).
 *
 * `kraahlRateId`, `providerRateId` and `ShippingRatesQuote.id` are all the
 * SAME value — the carrier rate object_id (Shippo `object_id`). The opaque
 * indirection was reverted 2026-06-25 (see
 * [[project_opaque_rate_id_revert_2026_06_25]]); the field names are kept as
 * aliases so consumers compile, and a follow-up should collapse them.
 */
export interface ShippingRateSelection {
  kraahlRateId: string;
  providerRateId: string;
  providerShipmentId: string;
  amount: number;
  currency: "USD" | "CAD";
}

/**
 * State FSM. `idle` is the initial / post-reset state. `loading` carries the
 * intent so the UI can show a "rating <city>…" affordance. `loaded` carries
 * `selectedId: null` until the buyer picks; carries the latest `intent` so a
 * re-render after `selectRate` still narrates which intent produced the list.
 * `error` carries both `code` (for branching) and `message` (server-supplied
 * human-readable text, surfaced verbatim).
 */
export type ShippingRatesState =
  | { kind: "idle" }
  | { kind: "loading"; intent: ShippingRateIntent }
  | {
      kind: "loaded";
      rates: ShippingRatesQuote[];
      selectedId: string | null;
      intent: ShippingRateIntent;
      /**
       * Seller's ship-from country (ISO-3166 alpha-2, UPPER-CASE) surfaced
       * verbatim from the server's `ShippingRatesResponse.fromCountry`.
       * `LiveRateSubflow` compares it against `intent.address.country` to
       * decide whether to show the "International rate may adjust at label
       * purchase" disclosure (HANDOFF_RELIABILITY_GAPS_2026_06_27
       * §"PR-G шаг 3" — estimate-vs-truth policy). Single SoT — the
       * checkout rate is an ESTIMATE; the ready-to-ship re-rate with
       * customs IS the source of truth, with the §I.2 delta-cap as the
       * safety net (`lib/shippo/ready-to-ship.ts::decideDeltaCap`).
       */
      fromCountry: string;
    }
  | {
      kind: "error";
      code: ShippingRatesErrorCode;
      message: string;
      intent: ShippingRateIntent;
      /**
       * Code-specific payload lifted from the route's `details` block. The
       * UI uses these for country-specific copy without re-parsing `message`.
       * Union member is conditional on `code`; callers must guard.
       *  - `OUT_OF_ORIGIN` → `{ originCountry: string }` (ISO alpha-2)
       *  - `NO_RATES_TO_DESTINATION` → `{ country: string }` (ISO alpha-2)
       *  - all other codes → `undefined`
       */
      details?:
        | { code: "OUT_OF_ORIGIN"; originCountry: string }
        | { code: "NO_RATES_TO_DESTINATION"; country: string };
    };

/** Return shape — separate `state` (read FSM) from actions (write callbacks). */
export interface UseShippingRatesResult {
  state: ShippingRatesState;
  /** Derived from `state` + `selectedId`. Null when no rate is currently selected. */
  selection: ShippingRateSelection | null;
  /** Fire a fetch. Aborts any in-flight one. Builder errors are pre-caught. */
  fetchRates: (intent: ShippingRateIntent) => void;
  /** Select one of the loaded rates by its `kraahlRateId`. No-op outside `loaded`. */
  selectRate: (kraahlRateId: string) => void;
  /** Abort in-flight + return to `idle`. */
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Reducer — event-style actions (react-state-design §14)
// ---------------------------------------------------------------------------

type Action =
  | { type: "FETCH_STARTED"; intent: ShippingRateIntent }
  | {
      type: "FETCH_LOADED";
      rates: ShippingRatesQuote[];
      intent: ShippingRateIntent;
      fromCountry: string;
    }
  | {
      type: "FETCH_ERRORED";
      code: ShippingRatesErrorCode;
      message: string;
      intent: ShippingRateIntent;
      details?:
        | { code: "OUT_OF_ORIGIN"; originCountry: string }
        | { code: "NO_RATES_TO_DESTINATION"; country: string };
    }
  | { type: "RATE_SELECTED"; kraahlRateId: string }
  | { type: "RESET" };

function unreachable(x: never): never {
  throw new Error(`useShippingRates: unreachable action: ${JSON.stringify(x)}`);
}

function reducer(state: ShippingRatesState, action: Action): ShippingRatesState {
  switch (action.type) {
    case "FETCH_STARTED":
      return { kind: "loading", intent: action.intent };
    case "FETCH_LOADED":
      return {
        kind: "loaded",
        rates: action.rates,
        selectedId: null,
        intent: action.intent,
        fromCountry: action.fromCountry,
      };
    case "FETCH_ERRORED":
      return {
        kind: "error",
        code: action.code,
        message: action.message,
        intent: action.intent,
        ...(action.details !== undefined ? { details: action.details } : {}),
      };
    case "RATE_SELECTED":
      if (state.kind !== "loaded") return state;
      // Defensive: ignore selections of ids not in the current list.
      if (!state.rates.some((r) => r.id === action.kraahlRateId)) return state;
      return { ...state, selectedId: action.kraahlRateId };
    case "RESET":
      return { kind: "idle" };
    default:
      return unreachable(action);
  }
}

// ---------------------------------------------------------------------------
// Error-body shape from the route — opaque envelope
// ---------------------------------------------------------------------------

interface RouteErrorBody {
  error?: unknown;
  code?: unknown;
  details?: unknown;
}

/**
 * Set of route-emitted codes. Any string outside this set on a non-2xx body is
 * normalised to `INTERNAL_ERROR` — the buyer-side hook refuses to invent new
 * union members from server strings (left-fix audit discipline:
 * [[feedback_left_fix_audit_downstream_branches]]).
 *
 * `OUT_OF_ORIGIN` + `NO_RATES_TO_DESTINATION` were added 2026-06-20 as the
 * origin-tier model (Option D) landed in the rates domain. A future left-fix
 * of the route's error vocabulary must extend this list in the SAME commit.
 *
 * `SHIPPING_SANCTIONED_DESTINATION` + `PARCEL_BELOW_CARRIER_MINIMUM` added
 * T13 2026-07-23 — both codes existed on the route already; this list (and
 * therefore the buyer-facing copy) had not caught up.
 */
const ROUTE_ERROR_CODE_LIST = [
  "UNAUTHORIZED",
  "MULTI_SELLER_FORBIDDEN",
  "VALIDATION_ERROR",
  "OUT_OF_ORIGIN",
  "NO_RATES_TO_DESTINATION",
  "INVALID_ADDRESS",
  "MISSING_PARCEL_DIMS",
  "MISSING_FROM_ADDRESS",
  "PROVIDER_ERROR",
  "INTERNAL_ERROR",
  "SHIPPING_SANCTIONED_DESTINATION",
  "PARCEL_BELOW_CARRIER_MINIMUM",
] as const satisfies readonly ShippingRatesErrorCode[];
const ROUTE_ERROR_CODES: ReadonlySet<ShippingRatesErrorCode> = new Set(ROUTE_ERROR_CODE_LIST);

function normaliseRouteCode(raw: unknown): ShippingRatesErrorCode {
  if (typeof raw !== "string") return "INTERNAL_ERROR";
  return ROUTE_ERROR_CODES.has(raw as ShippingRatesErrorCode)
    ? (raw as ShippingRatesErrorCode)
    : "INTERNAL_ERROR";
}

/**
 * Parse the route's `details` block for the two codes that carry country
 * payload. Defensive: a server contract drift returning a malformed shape
 * resolves to `undefined`, so the UI falls back to the generic `message`.
 */
function parseRouteDetails(
  code: ShippingRatesErrorCode,
  raw: unknown,
):
  | { code: "OUT_OF_ORIGIN"; originCountry: string }
  | { code: "NO_RATES_TO_DESTINATION"; country: string }
  | undefined {
  if (raw === null || typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;
  if (code === "OUT_OF_ORIGIN") {
    const c = obj.originCountry;
    return typeof c === "string" && c.length > 0
      ? { code: "OUT_OF_ORIGIN", originCountry: c }
      : undefined;
  }
  if (code === "NO_RATES_TO_DESTINATION") {
    const c = obj.country;
    return typeof c === "string" && c.length > 0
      ? { code: "NO_RATES_TO_DESTINATION", country: c }
      : undefined;
  }
  return undefined;
}

function readMessage(body: RouteErrorBody, fallback: string): string {
  return typeof body.error === "string" && body.error.length > 0
    ? body.error
    : fallback;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Race-safe FSM hook for buyer-side live rate fetch + selection.
 *
 * Concurrency-design §5 / react-state-design §10:
 *   AbortController per request + monotonic request-id (`requestIdRef`) +
 *   `latestStateRef` for stable-callback reads.
 *
 * Caller fires `fetchRates(intent)` explicitly — never auto-triggered by
 * `useEffect` (HARD GATE: no `useEffect` data fetching).
 */
export function useShippingRates(): UseShippingRatesResult {
  const [state, dispatch] = useReducer(reducer, { kind: "idle" });

  // latestStateRef: callbacks read state without re-binding on every change
  // (stable identities for downstream `useEffect([selectRate])` consumers).
  const latestStateRef = useRef<ShippingRatesState>(state);
  latestStateRef.current = state;

  // Active controller + monotonic id. Both refs because a change must NOT
  // trigger re-render — they're plumbing for cancellation, not view data.
  const controllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  // The only `useEffect` in this hook: abort the in-flight controller on
  // unmount. NOT a data fetcher. (HARD GATE rule.)
  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, []);

  const fetchRates = useCallback((intent: ShippingRateIntent) => {
    // Cancel the previous in-flight request first — single-flight by latest-
    // wins, the awaited promise will resolve with the abort signal set.
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const myId = ++requestIdRef.current;

    // Builder is a pure normaliser — never throws (Phase 1a → 2a left-fix:
    // destination gating moved server-side, see lib/checkout/build-shipping-
    // rates-request.ts). The defensive try/catch stays for surfacing any
    // future programmer bug as INTERNAL_ERROR rather than crashing the hook.
    let body: ReturnType<typeof buildShippingRatesRequest>;
    try {
      body = buildShippingRatesRequest(intent);
    } catch (err) {
      dispatch({
        type: "FETCH_ERRORED",
        code: "INTERNAL_ERROR",
        message: err instanceof Error ? err.message : "Builder error",
        intent,
      });
      return;
    }

    dispatch({ type: "FETCH_STARTED", intent });

    void (async () => {
      try {
        const res = await fetch("/api/shipping/rates", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        // Stale-resolve guard: a newer fetchRates may have superseded us
        // between dispatch(STARTED) and the await resolving. Discard silently.
        if (myId !== requestIdRef.current) return;

        if (!res.ok) {
          const errBody = (await res.json().catch(() => ({}))) as RouteErrorBody;
          if (myId !== requestIdRef.current) return;
          const code = normaliseRouteCode(errBody.code);
          const message = readMessage(errBody, `Request failed (HTTP ${res.status})`);
          const details = parseRouteDetails(code, errBody.details);
          // Browser-console breadcrumb — staging debugging when the buyer
          // says "I don't see rates". Server logs the same event server-side;
          // this surfaces it client-side without DevTools Network drilling.
          console.warn("[shipping-rates] error", { status: res.status, code, message, body: errBody });
          dispatch({
            type: "FETCH_ERRORED",
            code,
            message,
            intent,
            ...(details !== undefined ? { details } : {}),
          });
          return;
        }

        const raw = (await res.json().catch(() => null)) as unknown;
        if (myId !== requestIdRef.current) return;
        const parsed = ShippingRatesResponseSchema.safeParse(raw);
        if (!parsed.success) {
          console.warn("[shipping-rates] response shape invalid", { raw, issues: parsed.error.issues });
          dispatch({
            type: "FETCH_ERRORED",
            code: "RESPONSE_SHAPE_INVALID",
            message: "Server returned an unexpected response shape",
            intent,
          });
          return;
        }
        if (parsed.data.rates.length === 0) {
          console.warn("[shipping-rates] empty rates", { intent });
        }
        dispatch({
          type: "FETCH_LOADED",
          rates: parsed.data.rates,
          intent,
          // `fromCountry` is the server's resolved seller ship-from country.
          // Schema requires it (`z.string().min(2)`); above safeParse
          // guarantees presence. UPPER-CASE per producer contract.
          fromCountry: parsed.data.fromCountry.toUpperCase(),
        });
      } catch (err) {
        // Aborts are a happy-path outcome — discard silently
        // (concurrency-design §5.2 "cancellation is not a failure").
        if (controller.signal.aborted) return;
        if (myId !== requestIdRef.current) return;
        const message = err instanceof Error ? err.message : "Network error";
        console.warn("[shipping-rates] network error", { message, err });
        dispatch({
          type: "FETCH_ERRORED",
          code: "NETWORK_ERROR",
          message,
          intent,
        });
      }
    })();
  }, []);

  const selectRate = useCallback((kraahlRateId: string) => {
    // latestStateRef avoids a stale-closure bug if the consumer destructures
    // selectRate once and calls it across multiple state transitions.
    if (latestStateRef.current.kind !== "loaded") return;
    dispatch({ type: "RATE_SELECTED", kraahlRateId });
  }, []);

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    requestIdRef.current++;
    dispatch({ type: "RESET" });
  }, []);

  // Derive `selection` from state — no mirror state, no useEffect.
  const selection = useMemo<ShippingRateSelection | null>(() => {
    if (state.kind !== "loaded" || state.selectedId === null) return null;
    const rate = state.rates.find((r) => r.id === state.selectedId);
    if (!rate) return null;
    // Currency on the wire is `string` per the contract (Zod min(3)); the
    // domain narrows to `'USD' | 'CAD'` (Rate ADT, lib/shipping/types.ts).
    // We re-narrow here defensively — anything outside the NA whitelist is a
    // programmer bug given §A "NA only", so we silently coerce and let the
    // consumer's `currency` switch fail loud if it's somehow wrong.
    const currency: "USD" | "CAD" = rate.currency === "CAD" ? "CAD" : "USD";
    return {
      kraahlRateId: rate.id,
      providerRateId: rate.id,
      providerShipmentId: rate.shipmentId,
      amount: rate.amount,
      currency,
    };
  }, [state]);

  return { state, selection, fetchRates, selectRate, reset };
}
